/**
 * 键盘事件处理模块：按"远程输入协议 v2"把本地键盘输入映射到远端。
 *
 * 协议规则：docs/reference/protocol/remote-input.md
 *
 * 两条并行轨：
 *   - keyDown / keyUp：物理位轨（携带 event.code、event.key、modifiers，含 altGraph）
 *   - textInput：文本/Unicode 轨（覆盖普通打字、AltGraph 字符与 IME commit）
 *
 * 所有消息均通过 control 通道（可靠有序）发送，sender 在该模块中负责：
 *   - AltGraph 优先级判定
 *   - Numpad 双轨抑制（追踪最近一次 keydown.code）
 *   - releaseAll / mode / clipboard 等控制消息封装
 */

import { hasNegotiatedCapability } from "./negotiation";

const PROTOCOL_VERSION = 2 as const;

const IS_DEV = import.meta.env.DEV;

/**
 * 协议 v2 §7.2 keyDown / keyUp 负载。
 */
type KeyEventPayload = {
	v: typeof PROTOCOL_VERSION;
	type: "keyDown" | "keyUp";
	code: string;
	key: string;
	modifiers: {
		shift: boolean;
		ctrl: boolean;
		alt: boolean;
		meta: boolean;
		altGraph: boolean;
	};
	repeat?: boolean;
	t?: number;
};

/**
 * 协议 v2 §7.2 textInput 负载。
 */
type TextInputPayload = {
	v: typeof PROTOCOL_VERSION;
	type: "textInput";
	text: string;
	t?: number;
};

/**
 * 协议 v2 §7.4 releaseAll 负载。
 */
type ReleaseAllPayload = {
	v: typeof PROTOCOL_VERSION;
	type: "releaseAll";
	t?: number;
};

/**
 * 协议 v2 §7.4 mode 负载。
 */
type ModePayload = {
	v: typeof PROTOCOL_VERSION;
	type: "mode";
	input: "text" | "raw";
	t?: number;
};

/**
 * 协议 v2 §7.3 clipboard 负载。
 */
type ClipboardSetPayload = {
	v: typeof PROTOCOL_VERSION;
	type: "clipboard:set";
	mime: "text/plain";
	text: string;
	t?: number;
};
type ClipboardRequestPayload = {
	v: typeof PROTOCOL_VERSION;
	type: "clipboard:request";
	t?: number;
};

/**
 * 协议 v2 §5 hello 负载。
 */
type HelloPayload = {
	v: typeof PROTOCOL_VERSION;
	type: "hello";
	side: "sender";
	capabilities: string[];
	keyboardLayout?: string;
	t?: number;
};

type ControlPayload =
	| KeyEventPayload
	| TextInputPayload
	| ReleaseAllPayload
	| ModePayload
	| ClipboardSetPayload
	| ClipboardRequestPayload
	| HelloPayload;

/**
 * 判断数据通道是否可写。
 * @param dataChannel
 */
const canSend = (
	dataChannel: RTCDataChannel | null,
): dataChannel is RTCDataChannel =>
	!!dataChannel && dataChannel.readyState === "open";

/**
 * 统一向 control 通道写入 JSON 协议消息。
 * @param dataChannel
 * @param payload
 */
const sendJson = (
	dataChannel: RTCDataChannel | null,
	payload: ControlPayload,
) => {
	if (!canSend(dataChannel)) return;
	if (IS_DEV) console.log("[remote-input → control]", payload);
	try {
		dataChannel.send(JSON.stringify(payload));
	} catch (error) {
		console.warn("[keyboard] send failed:", error);
	}
};

/**
 * 提取浏览器键盘事件的修饰键瞬时状态（含 altGraph）。
 * @param event
 */
const extractModifiers = (event: KeyboardEvent) => ({
	shift: event.shiftKey,
	ctrl: event.ctrlKey,
	alt: event.altKey,
	meta: event.metaKey,
	// AltGraph 优先级见协议 §8：Windows 上 AltGr 会被合成为 ctrl+alt，
	// receiver 依赖该字段判别"AltGr 文本输入意图" vs "真 Ctrl/Alt 修饰组合"。
	altGraph:
		typeof event.getModifierState === "function"
			? event.getModifierState("AltGraph")
			: false,
});

/**
 * 浏览器默认会触发的本地快捷键集合。
 * 远端控制场景下需要拦截，避免按下 Ctrl+W / F5 等真的影响本地浏览器。
 * 调试快捷键（F12、Ctrl+Shift+I）保留，便于排查问题。
 */
const shouldPreventDefault = (event: KeyboardEvent): boolean => {
	const { key, ctrlKey, metaKey, altKey, shiftKey } = event;

	// AltGraph 文本输入意图（协议 §8）必须放行：preventDefault 会阻止隐藏 contentEditable
	// 上的 input 事件，进而让 textInput 链路断裂，欧洲布局上 `AltGr+2 → @/€` 无法落字。
	const altGraph =
		typeof event.getModifierState === "function" &&
		event.getModifierState("AltGraph");
	if (altGraph) return false;

	// 放行 DevTools
	if (key === "F12") return false;
	if ((ctrlKey || metaKey) && shiftKey && (key === "I" || key === "i"))
		return false;
	if ((ctrlKey || metaKey) && shiftKey && (key === "C" || key === "c"))
		return false;
	if ((ctrlKey || metaKey) && shiftKey && (key === "J" || key === "j"))
		return false;

	// 任何含修饰键的组合都拦截（避免触发浏览器/系统快捷键）
	if (ctrlKey || metaKey || altKey) return true;

	// 功能键、Tab、退格在远控时也应该走到远端，而不是本地浏览器
	if (key === "Tab" || key === "Backspace") return true;
	if (/^F\d+$/.test(key)) return true;

	return false;
};

/**
 * 处理 keydown，发送一条 keyDown 物理位消息。
 * @param dataChannel control 通道
 * @param event
 */
export const keyDownEventHandler = (
	dataChannel: RTCDataChannel | null,
	event: KeyboardEvent,
) => {
	if (shouldPreventDefault(event)) {
		event.preventDefault();
	}
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "keyDown",
		code: event.code,
		key: event.key,
		modifiers: extractModifiers(event),
		repeat: event.repeat || undefined,
		t: Date.now(),
	});
};

/**
 * 处理 keyup，发送一条 keyUp 物理位消息。
 * @param dataChannel control 通道
 * @param event
 */
export const keyUpEventHandler = (
	dataChannel: RTCDataChannel | null,
	event: KeyboardEvent,
) => {
	if (shouldPreventDefault(event)) {
		event.preventDefault();
	}
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "keyUp",
		code: event.code,
		key: event.key,
		modifiers: extractModifiers(event),
		t: Date.now(),
	});
};

/**
 * 处理普通文本输入（来自隐藏 input 的 `inputType === 'insertText'`）。
 *
 * 协议 §8 触发规则：
 *   - `!meta && (altGraph || (!ctrl && !alt))`，且最近一次 keydown.code 不以 `Numpad` 开头
 *   - AltGraph 优先级高于 Ctrl/Alt，欧洲字符（@、€、\ 等）仍按 textInput 输入
 *   - Numpad 双轨抑制：text 模式 receiver 对所有 `Numpad*` 走 scancode，
 *     不抑制会导致双重落字（如 NumLock 开启时按 `Numpad1` 落出 `11`）
 *
 * @param dataChannel control 通道
 * @param text
 * @param modifiersDown 当前修饰键瞬时状态
 * @param lastKeyDownCode 最近一次 keydown 的 `event.code`（用于 Numpad 去重）
 */
export const textInputHandler = (
	dataChannel: RTCDataChannel | null,
	text: string,
	modifiersDown: {
		ctrl: boolean;
		alt: boolean;
		meta: boolean;
		altGraph: boolean;
	},
	lastKeyDownCode: string | null,
) => {
	if (!text) return;
	if (modifiersDown.meta) return;
	// AltGraph 高优先：即使 ctrl/alt 同时为 true，也应当作为文本输入意图
	if (!modifiersDown.altGraph && (modifiersDown.ctrl || modifiersDown.alt))
		return;
	// Numpad 双轨抑制
	if (lastKeyDownCode?.startsWith("Numpad")) return;

	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "textInput",
		text,
		t: Date.now(),
	});
};

/**
 * 处理 IME composition end（已 commit 的最终文本）。
 *
 * 协议 §8：`compositionend` 直接发送，不受 Numpad / 修饰键抑制约束。
 * @param dataChannel control 通道
 * @param event
 */
export const compositionEndHandler = (
	dataChannel: RTCDataChannel | null,
	event: CompositionEvent,
) => {
	const text = event.data;
	if (!text) return;
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "textInput",
		text,
		t: Date.now(),
	});
};

/**
 * 发送握手 hello（协议 §5 必须，双端在 open 后**首先**发送）。
 *
 * 默认能力声明：必备的 `scancode` / `unicode` / `mode`，外加 sender 在本模块中已经实现的
 * `wheel` / `relativeMouse` / `clipboardText`。
 * @param dataChannel control 通道
 */
export const sendHello = (dataChannel: RTCDataChannel | null) => {
	const layout =
		typeof navigator !== "undefined" && navigator.language
			? navigator.language
			: undefined;
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "hello",
		side: "sender",
		keyboardLayout: layout,
		capabilities: [
			"scancode",
			"unicode",
			"mode",
			"wheel",
			"relativeMouse",
			"clipboardText",
		],
		t: Date.now(),
	});
};

/**
 * 发送 releaseAll：要求 receiver 释放当前所有按下的键和鼠标按钮。
 *
 * 协议 §7.4 触发时机：
 *   - 远控画面失去焦点 / 标签页隐藏；
 *   - mode 切换前；
 *   - 用户显式触发"释放所有按键"操作；
 *   - sender 自身重连前。
 * @param dataChannel control 通道
 */
export const sendReleaseAll = (dataChannel: RTCDataChannel | null) => {
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "releaseAll",
		t: Date.now(),
	});
};

/**
 * 发送 mode 切换。协议 §6 要求切换前 sender 必须先发 releaseAll。
 * @param dataChannel control 通道
 * @param input 目标模式
 */
export const sendMode = (
	dataChannel: RTCDataChannel | null,
	input: "text" | "raw",
) => {
	sendReleaseAll(dataChannel);
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "mode",
		input,
		t: Date.now(),
	});
};

/**
 * 主动通知对方"我的剪贴板已变为以下内容，请同步"。仅 text/plain。
 * @param dataChannel control 通道
 * @param text
 */
export const sendClipboardSet = (
	dataChannel: RTCDataChannel | null,
	text: string,
) => {
	// 协议 §5.1 capability gating：双方都声明 clipboardText 才允许发送
	if (!hasNegotiatedCapability("clipboardText")) return;
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "clipboard:set",
		mime: "text/plain",
		text,
		t: Date.now(),
	});
};

/**
 * 主动拉取对方当前剪贴板内容。对方应当回应一条 `clipboard:set`。
 * @param dataChannel control 通道
 */
export const sendClipboardRequest = (dataChannel: RTCDataChannel | null) => {
	if (!hasNegotiatedCapability("clipboardText")) return;
	sendJson(dataChannel, {
		v: PROTOCOL_VERSION,
		type: "clipboard:request",
		t: Date.now(),
	});
};

/**
 * 发送 Ctrl + Alt + Del 组合键。
 * @param dataChannel control 通道
 */
export const sendCAD = (dataChannel: RTCDataChannel | null) => {
	if (!canSend(dataChannel)) return;
	const stamp = Date.now();
	const noMod = {
		shift: false,
		ctrl: false,
		alt: false,
		meta: false,
		altGraph: false,
	};

	// 顺序：Ctrl down, Alt down, Delete down, Delete up, Alt up, Ctrl up
	const seq: KeyEventPayload[] = [
		{
			v: PROTOCOL_VERSION,
			type: "keyDown",
			code: "ControlLeft",
			key: "Control",
			modifiers: { ...noMod, ctrl: true },
			t: stamp,
		},
		{
			v: PROTOCOL_VERSION,
			type: "keyDown",
			code: "AltLeft",
			key: "Alt",
			modifiers: { ...noMod, ctrl: true, alt: true },
			t: stamp,
		},
		{
			v: PROTOCOL_VERSION,
			type: "keyDown",
			code: "Delete",
			key: "Delete",
			modifiers: { ...noMod, ctrl: true, alt: true },
			t: stamp,
		},
		{
			v: PROTOCOL_VERSION,
			type: "keyUp",
			code: "Delete",
			key: "Delete",
			modifiers: { ...noMod, ctrl: true, alt: true },
			t: stamp,
		},
		{
			v: PROTOCOL_VERSION,
			type: "keyUp",
			code: "AltLeft",
			key: "Alt",
			modifiers: { ...noMod, ctrl: true },
			t: stamp,
		},
		{
			v: PROTOCOL_VERSION,
			type: "keyUp",
			code: "ControlLeft",
			key: "Control",
			modifiers: noMod,
			t: stamp,
		},
	];

	for (const msg of seq) sendJson(dataChannel, msg);
};
